import { useState, useMemo, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useFinancialYear } from "../../contexts/FinancialYearContext";
import { usePermissions } from "../../contexts/PermissionsContext";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import {
  CalendarDays,
  ListTodo,
  NotebookPen,
  MoreHorizontal,
  Calculator,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  LogOut,
  User,
  ChevronDown,
  BookOpen,
  CalendarRange,
  ShieldCheck,
  Sparkles,
  Bell,
  Star,
  Store,
  Smartphone,
  ArrowLeftRight,
  LayoutGrid,
} from "lucide-react";
import NotificationBell from "../notifications/NotificationBell";
import { useNotifications } from "../../contexts/NotificationContext";
import BottomNavBar from "./BottomNavBar";
import SwipeNavigator from "./SwipeNavigator";
import TabBar from "./TabBar";
import WorkspaceToggle from "./WorkspaceToggle";
import NowBar from "./NowBar";
import ErpContextMenu from "./ErpContextMenu";
import QuickFilterBar from "./QuickFilterBar";
import AIChatDialog from "../ai/AIChatDialog";
import CalculatorPanel from "./CalculatorPanel";
import PentsOverlay from "../PentsOverlay";
import HandoffSwitchDialog from "./HandoffSwitchDialog";
import HandoffIncomingDialog from "./HandoffIncomingDialog";
import { useHandoff } from "../../contexts/HandoffContext";
import { navigation, getTitleForHref } from "../../config/navigation";
import { useFrameActive, useTabs } from "../../contexts/TabsContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [envLabel, setEnvLabel] = useState("");
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState("");
  const loadedVersionRef = useRef(null);
  const loadedBundleRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { financialYears, selectedYear, setSelectedYear } = useFinancialYear();
  const { hasPermission, hasFullAccess, userRole } = usePermissions();
  // False while this layout lives in a hidden keep-alive tab frame.
  const frameActive = useFrameActive();
  const { openInNewTab } = useTabs();
  const { openPicker: openSwitchPicker } = useHandoff();
  const { unreadCount } = useNotifications();

  // Check if current user is Owner, Admin, or Developer (can access admin-only items)
  const isAdminUser = hasFullAccess || ["owner", "admin", "developer"].includes(userRole?.toLowerCase());

  // App version + upgrade watch. Two independent signals, either of which flips
  // the NowBar to a "refresh" prompt when a new build is live but the user
  // is still on the old one:
  //   1. env-info version STRING — drives the profile badge and, if you bump the
  //      string, puts the new number in the message.
  //   2. asset-manifest SIGNATURE — the CRA build writes content-hashed bundle
  //      names here; they change on ANY frontend deploy, so this auto-detects
  //      upgrades with no manual version bump. (Absent on the dev server → the
  //      check just no-ops, so there are no false positives while developing.)
  useEffect(() => {
    let cancelled = false;

    const checkVersion = async () => {
      try {
        const r = await axios.get(`${API}/env-info`);
        const v = r.data?.version || "V7.8.66";
        if (cancelled) return;
        if (loadedVersionRef.current === null) {
          loadedVersionRef.current = v;
          setEnvLabel(v);
        } else if (v !== loadedVersionRef.current) {
          setLatestVersion(v); // a bumped string → show the number in the prompt
          setUpdateAvailable(true);
        }
      } catch {
        if (!cancelled && loadedVersionRef.current === null) {
          loadedVersionRef.current = "V7.8.66";
          setEnvLabel("V7.8.66");
        }
      }
    };

    const manifestUrl = `${process.env.PUBLIC_URL || ""}/asset-manifest.json`;
    const checkBundle = async () => {
      try {
        const res = await fetch(`${manifestUrl}?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        const sig = JSON.stringify(data.files || data.entrypoints || data);
        if (cancelled) return;
        if (loadedBundleRef.current === null) {
          loadedBundleRef.current = sig;
        } else if (sig !== loadedBundleRef.current) {
          setUpdateAvailable(true); // hashes changed → a new build is deployed
        }
      } catch {
        /* manifest unreachable (e.g. dev server) — skip auto-detection */
      }
    };

    const tick = () => { checkVersion(); checkBundle(); };
    tick();
    const id = setInterval(tick, 2 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Fetch pending approvals count for users who can view approvals.
  // Paused while this layout sits in a hidden tab frame (no point polling).
  useEffect(() => {
    if (!frameActive) return;
    const fetchPendingApprovals = async () => {
      if (!hasPermission('view_approvals')) return;

      try {
        const token = localStorage.getItem('foundry-token');
        const userRole = user?.user_metadata?.role || user?.role || '';

        const response = await axios.get(`${API}/approvals/requests/count`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { role: userRole }
        });

        setPendingApprovalsCount(response.data?.count || 0);
      } catch (err) {
        console.error('Error fetching pending approvals:', err);
      }
    };

    fetchPendingApprovals();
    // Refresh every 30 seconds + on tab focus + when any in-app action fires
    // the 'approvals-changed' event so the sidebar badge updates instantly.
    const interval = setInterval(fetchPendingApprovals, 30000);
    const onFocus = () => fetchPendingApprovals();
    const onVisible = () => { if (!document.hidden) fetchPendingApprovals(); };
    const onChanged = () => fetchPendingApprovals();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('approvals-changed', onChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('approvals-changed', onChanged);
    };
  }, [user, hasPermission, frameActive]);

  // Filter navigation based on permissions
  const filteredNavigation = useMemo(() => {
    return navigation
      .map(item => {
        // If item has children, filter them based on permissions
        if (item.children) {
          const filteredChildren = item.children.filter(child => {
            // Check admin-only restriction first
            if (child.adminOnly && !isAdminUser) return false;
            // "anyPermission" — show if the user has at least one of the listed perms
            if (child.anyPermission) return child.anyPermission.some(p => hasPermission(p));
            // Then check single permission
            return !child.permission || hasPermission(child.permission);
          });
          // Only include parent if it has visible children
          if (filteredChildren.length === 0) return null;
          // Don't surface a parent whose ONLY visible children are permission-free
          // "personal" items (e.g. System → Settings / Quick Access). This keeps a
          // restricted single-page user (e.g. only "view_store_consumption") locked
          // down, while normal users — who have at least one gated child — keep them.
          const hasGatedChild = filteredChildren.some(c => c.permission || c.anyPermission);
          if (!hasGatedChild && !isAdminUser) return null;
          return { ...item, children: filteredChildren };
        }
        // For items without children, check admin-only and permission
        if (item.adminOnly && !isAdminUser) return null;
        if (item.permission && !hasPermission(item.permission)) {
          return null;
        }
        return item;
      })
      .filter(Boolean);
  }, [hasPermission, isAdminUser]);

  // First page the user is actually allowed to see (used to redirect users who
  // can't access the dashboard away from "/").
  const firstAccessibleHref = useMemo(() => {
    const first = filteredNavigation[0];
    if (!first) return null;
    return first.children ? first.children[0]?.href : first.href;
  }, [filteredNavigation]);

  // If a restricted user lands on "/" without dashboard access, send them to
  // their first allowed page instead of an empty dashboard.
  useEffect(() => {
    if (
      location.pathname === "/" &&
      !hasPermission("view_dashboard") &&
      firstAccessibleHref &&
      firstAccessibleHref !== "/"
    ) {
      navigate(firstAccessibleHref, { replace: true });
    }
  }, [location.pathname, hasPermission, firstAccessibleHref, navigate]);

  // Global-controls cluster. AI / Notifications / Approvals / Calendar live in
  // the "More" (⋯) menu; Notifications and Approvals only step OUT of it as their
  // own icon while they have something active (unread / pending), so the capsule
  // stays quiet by default. Shared round-pill button base:
  const ctrlBtn =
    "relative inline-flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-card transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";
  const canApprove = hasPermission('view_approvals');
  const showBell = unreadCount > 0;
  const showApprovals = canApprove && pendingApprovalsCount > 0;
  // Kept MOUNTED and animated via width/scale/opacity (not conditionally
  // rendered) so an icon slides in AND slides back out — unmounting would snap
  // it away with no exit animation. No overflow-hidden: it would clip the badge.
  const revealCls = (on) =>
    `inline-flex transition-all duration-300 ease-out ${
      on
        ? "w-8 mr-0.5 opacity-100 scale-100"
        : "w-0 mr-0 opacity-0 scale-50 pointer-events-none"
    }`;
  const iconControls = (
    <div className="inline-flex items-center rounded-full bg-muted/50 p-0.5 ring-1 ring-inset ring-border/40">
      <div className={revealCls(showBell)} aria-hidden={!showBell}>
        <NotificationBell triggerClassName="text-muted-foreground hover:text-foreground hover:bg-card" />
      </div>
      {canApprove && (
        <div className={revealCls(showApprovals)} aria-hidden={!showApprovals}>
          <button
            type="button"
            className={ctrlBtn}
            onClick={() => navigate('/system/approvals')}
            title="Pending Approvals"
            tabIndex={showApprovals ? 0 : -1}
          >
            <ShieldCheck className="w-[18px] h-[18px]" />
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold ring-2 ring-background">
              {pendingApprovalsCount > 99 ? '99+' : pendingApprovalsCount}
            </span>
          </button>
        </div>
      )}
      {/* "More" — AI, notifications, approvals, calendar + small personal tools.
          Add new utilities here rather than widening the capsule. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={ctrlBtn}
            title="More"
            data-testid="more-btn"
          >
            <MoreHorizontal className="w-[18px] h-[18px]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {hasPermission('use_ai_assistant') && (
            <DropdownMenuItem onClick={() => setAiChatOpen(true)} data-testid="ai-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              AI Assistant
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => navigate('/system/notifications')} data-testid="notifications-btn">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
            {unreadCount > 0 && (
              <span className="ml-auto text-[10px] font-semibold text-red-500 tabular-nums">{unreadCount}</span>
            )}
          </DropdownMenuItem>
          {canApprove && (
            <DropdownMenuItem onClick={() => navigate('/system/approvals')} data-testid="approvals-btn">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Approvals
              {pendingApprovalsCount > 0 && (
                <span className="ml-auto text-[10px] font-semibold text-red-500 tabular-nums">{pendingApprovalsCount}</span>
              )}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => navigate('/calendar')} data-testid="calendar-btn">
            <CalendarDays className="w-4 h-4 mr-2" />
            Calendar
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {/* Personal TODO list. Ungated like the Calendar — every user has their
              own private list; the server scopes it to their user id. */}
          <DropdownMenuItem onClick={() => navigate('/todos')} data-testid="todos-btn">
            <ListTodo className="w-4 h-4 mr-2" />
            My Todo List
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/notes')} data-testid="notes-btn">
            <NotebookPen className="w-4 h-4 mr-2" />
            My Notes
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setCalcOpen(true)} data-testid="calculator-btn">
            <Calculator className="w-4 h-4 mr-2" />
            Calculator
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const fyMenuItems = (
    <>
      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
        Financial Year
      </div>
      <DropdownMenuSeparator />
      {financialYears.map((fy) => (
        <DropdownMenuItem
          key={fy.id}
          onClick={() => setSelectedYear(fy)}
          className={selectedYear?.id === fy.id ? "bg-accent" : ""}
        >
          <div className="flex items-center justify-between w-full">
            <span>{fy.fy_name}</span>
            {fy.is_current && (
              <span className="text-xs text-green-600 font-medium">Current</span>
            )}
          </div>
        </DropdownMenuItem>
      ))}
    </>
  );

  const userMenuItems = (
    <>
      <div className="px-2 py-1.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <button
          type="button"
          data-testid="switch-device-btn"
          onClick={(e) => { e.stopPropagation(); setUserMenuOpen(false); openSwitchPicker(); }}
          className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <ArrowLeftRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <DropdownMenuSeparator />
      <div className="px-2 py-1.5">
        <p className="text-xs font-semibold text-muted-foreground mb-1.5">
          Theme
        </p>
        <div className="grid grid-cols-3 gap-1 rounded-md bg-muted p-1">
          {[
            { value: "light", icon: Sun, label: "Light" },
            { value: "dark", icon: Moon, label: "Dark" },
            { value: "system", icon: Monitor, label: "System" },
          ].map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              type="button"
              title={label}
              onClick={() => setTheme(value)}
              data-testid={`theme-${value}`}
              className={`flex items-center justify-center rounded-sm py-1.5 transition-colors ${
                theme === value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      </div>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => navigate("/docs")} data-testid="docs-link">
        <BookOpen className="w-4 h-4 mr-2" />
        Docs
      </DropdownMenuItem>
      {hasPermission("access_bnb") && (
        <DropdownMenuItem onClick={() => navigate("/bnb")} data-testid="bnb-link">
          <Store className="w-4 h-4 mr-2" />
          B&amp;B
        </DropdownMenuItem>
      )}
      {hasPermission("access_banamob") && (
        <DropdownMenuItem onClick={() => navigate("/banamob")} data-testid="banamob-link">
          <Smartphone className="w-4 h-4 mr-2" />
          BANAMOB
        </DropdownMenuItem>
      )}
      {/* Tools hub (DocMatch, ...). Ungated — see the /tools route. */}
      <DropdownMenuItem onClick={() => navigate("/tools")} data-testid="more-tools-link">
        <LayoutGrid className="w-4 h-4 mr-2" />
        More
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={logout}
        className="text-destructive focus:text-destructive"
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Logout
      </DropdownMenuItem>
    </>
  );

  return (
    <div className="min-h-screen bg-background noise-texture">
      {/* ── Global top region — "Calm Premium" (One UI) ─────────────────────
          Top-left is one independent brand square (logo + ERP/Docs toggle) that
          spans both rows and merges into the sidebar column below. The frosted
          right column stacks the NowBar + controls over the tab strip. */}
      <div className="sticky top-0 z-[190] flex items-stretch">
        {/* LEFT — independent brand square (desktop); solid card so it reads as
            one block continuous with the sidebar directly beneath it. */}
        <div className="hidden lg:flex w-64 flex-shrink-0 flex-col justify-center gap-2.5 px-4 bg-card border-r border-border">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to="/"
              aria-label="Bana ERP home"
              className="flex items-center gap-2.5 min-w-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 ring-1 ring-primary/20 overflow-hidden flex-shrink-0">
                <img src="/bana-logo.jpeg" alt="Bana" className="w-6 h-6 rounded object-contain" />
              </span>
              <span className="font-heading font-bold text-base tracking-tight truncate">BANA ERP</span>
            </Link>
            <span
              data-testid="app-version"
              className={`flex-shrink-0 font-mono tabular-nums text-[10px] leading-none rounded px-1.5 py-0.5 ${envLabel.startsWith("BETA") ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300" : "bg-muted text-muted-foreground"}`}
            >
              {envLabel || "V7.8.66"}
            </span>
          </div>
          <WorkspaceToggle active="erp" className="w-full [&>button]:flex-1 [&>button]:justify-center" />
        </div>

        {/* RIGHT — frosted bar column: NowBar row over the tab strip */}
        <div className="relative flex-1 min-w-0 flex flex-col bg-background/70 supports-[backdrop-filter]:bg-background/60 backdrop-blur-xl border-b border-border/60">
          {/* One UI top-light hairline */}
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/50 dark:bg-white/[0.04]" />

          {/* Row 1 — NowBar + global controls (compact on desktop) */}
          <div className="h-14 lg:h-12 flex items-center gap-2 sm:gap-3 px-2 sm:px-4">
            {/* Mobile brand (desktop uses the left square) */}
            <div className="flex lg:hidden items-center gap-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <Link to="/" aria-label="Bana ERP home" className="flex items-center flex-shrink-0">
                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 ring-1 ring-primary/20 overflow-hidden">
                  <img src="/bana-logo.jpeg" alt="Bana" className="w-6 h-6 rounded object-contain" />
                </span>
              </Link>
            </div>
            <NowBar
              className="flex-1 hidden sm:flex"
              pendingApprovalsCount={pendingApprovalsCount}
              updateAvailable={updateAvailable}
              latestVersion={latestVersion}
            />
            {/* Mobile page title (the NowBar is hidden below sm) */}
            <h1 className="sm:hidden font-heading font-semibold text-sm truncate flex-1 min-w-0">
              {getTitleForHref(location.pathname + location.search)}
            </h1>

            <div className="flex items-center gap-2 flex-shrink-0">
              {iconControls}

              <span className="h-5 w-px bg-border/60 mx-1" />

              {/* Financial Year Selector */}
              {financialYears.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="fy-selector"
                      className="inline-flex items-center gap-1.5 h-9 px-2.5 sm:px-3 rounded-full border border-border/70 bg-card text-xs font-medium text-foreground hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      <CalendarRange className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="hidden sm:inline">{selectedYear?.fy_name || "Select FY"}</span>
                      <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden sm:inline" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {fyMenuItems}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* User menu */}
              <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="user-menu"
                    className="inline-flex items-center gap-2 h-9 pl-1 pr-2 rounded-full hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary">
                      <User className="w-4 h-4" />
                    </span>
                    <span className="hidden md:inline text-sm font-medium text-foreground max-w-[120px] truncate">
                      {user?.name}
                    </span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground hidden sm:inline" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {userMenuItems}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Row 2 — browser-style tab strip (desktop only), taller than Row 1 */}
          <div className="hidden lg:flex items-center h-11 border-t border-border/60 px-2 pointer-events-auto">
            <TabBar />
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-[195] bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — below the global bar (+ tab strip) on desktop; full-height
          overlay on mobile (rides above the global bar via the higher z). */}
      <aside
        className={`fixed top-0 lg:top-[var(--app-header-h)] left-0 z-[200] lg:z-[120] h-screen lg:h-[calc(100vh-var(--app-header-h))] w-64 bg-card border-r border-border transform transition-transform duration-200 ease-out lg:translate-x-0 flex flex-col ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        {/* Mobile-only header (on desktop the brand lives in the global bar) */}
        <div className="lg:hidden h-14 flex-shrink-0 flex items-center justify-between px-4 border-b border-border">
          <Link to="/" className="flex items-center gap-2" onClick={() => setSidebarOpen(false)}>
            <img src="/bana-logo.jpeg" alt="Bana" className="w-8 h-8 rounded-sm object-contain" />
            <span className="font-heading font-bold text-lg tracking-tight">BANA ERP</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="scrollbar-hide flex-1 p-2 space-y-1 overflow-y-auto overflow-x-hidden pb-20 lg:pb-8">
          {filteredNavigation.map((item) => {
            if (item.children) {
              const isOpen = item.children.some(child => (location.pathname + location.search) === child.href) || location.pathname.startsWith(item.href);
              return (
                <Collapsible key={item.name} defaultOpen={isOpen} className="group">
                  <CollapsibleTrigger className={`flex w-full items-center justify-between px-3 py-2 text-sm font-medium rounded-sm transition-colors duration-150 ${isOpen ? "text-primary bg-primary/10" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                    <div className="flex items-center gap-3">
                      <item.icon className="w-4 h-4" />
                      {item.name}
                    </div>
                    <ChevronDown className="w-4 h-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-9 pr-2 space-y-1 pt-1">
                    {item.children.map((child) => {
                      // Non-interactive sub-heading — labels a group of related
                      // items within this single-level menu (e.g. "Purchase"
                      // under Accounting) without a real nested submenu, which
                      // this sidebar doesn't support.
                      if (child.divider) {
                        return (
                          <div
                            // Prefixed: a divider may share its label with an
                            // item (Reports has a "Production" group and report).
                            key={`divider:${child.name}`}
                            className="px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70"
                          >
                            {child.name}
                          </div>
                        );
                      }
                      const isChildActive = (location.pathname + location.search) === child.href || location.pathname === child.href;
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium rounded-sm transition-colors ${isChildActive ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground"}`}
                          onClick={() => setSidebarOpen(false)}
                          onMouseDown={(e) => {
                            if (e.button === 1 && e.ctrlKey) e.preventDefault(); // ctrl+middle-click opens an ERP tab instead
                          }}
                          onAuxClick={(e) => {
                            if (e.button === 1 && e.ctrlKey) {
                              e.preventDefault(); // stop the browser's own new-tab default
                              openInNewTab(child.href, child.name);
                            }
                            // plain middle-click: let the browser open its native new tab
                          }}
                          draggable
                          onDragStart={(e) => {
                            // Drag this module onto the tab strip to open it in a new tab
                            // (same effect as ctrl+middle-click) — Chrome-style bookmark drag.
                            e.dataTransfer.effectAllowed = "copy";
                            e.dataTransfer.setData("application/x-erp-tab", JSON.stringify({ href: child.href, title: child.name }));
                            e.dataTransfer.setData("text/plain", child.href);
                          }}
                        >
                          {child.icon && <child.icon className="w-3.5 h-3.5" />}
                          {child.name}
                          {(child.master || child.starred) && <Star className="w-2.5 h-2.5 text-amber-500 fill-amber-500 flex-shrink-0 ml-auto" />}
                        </Link>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              );
            }

            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors duration-150 ${isActive
                  ? "bg-primary/10 text-primary border-l-2 border-primary -ml-[2px] pl-[14px]"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                onClick={() => setSidebarOpen(false)}
                onMouseDown={(e) => {
                  if (e.button === 1 && e.ctrlKey) e.preventDefault(); // ctrl+middle-click opens an ERP tab instead
                }}
                onAuxClick={(e) => {
                  if (e.button === 1 && e.ctrlKey) {
                    e.preventDefault(); // stop the browser's own new-tab default
                    openInNewTab(item.href, item.name);
                  }
                  // plain middle-click: let the browser open its native new tab
                }}
                draggable
                onDragStart={(e) => {
                  // Drag this module onto the tab strip to open it in a new tab
                  // (same effect as ctrl+middle-click) — Chrome-style bookmark drag.
                  e.dataTransfer.effectAllowed = "copy";
                  e.dataTransfer.setData("application/x-erp-tab", JSON.stringify({ href: item.href, title: item.name }));
                  e.dataTransfer.setData("text/plain", item.href);
                }}
              >
                <item.icon className="w-4 h-4" />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Page content */}
        <main className="p-2 sm:p-4 md:p-6 lg:p-8 pb-24 lg:pb-8">
          <SwipeNavigator>{children}</SwipeNavigator>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <BottomNavBar />

      {/* AI Chat Dialog */}
      <AIChatDialog open={aiChatOpen} onOpenChange={setAiChatOpen} />

      {/* Floating mini calculator (non-modal — stays up while you read figures) */}
      <CalculatorPanel open={calcOpen} onClose={() => setCalcOpen(false)} />

      <PentsOverlay />

      {/* Cross-device "Switch" — picker (sender) + accept/reject prompt (receiver) */}
      <HandoffSwitchDialog />
      <HandoffIncomingDialog />

      {/* Custom right-click menu (Ctrl+right-click = real browser menu) */}
      <ErpContextMenu />

      {/* Active Quick Filters (chips + clear) */}
      <QuickFilterBar />
    </div>
  );
};
